import { useEffect, useRef, useState } from "react";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import CircularProgress from "./CircularProgress";
import { GetImages } from "../../utils/GetImages";
import "./FileUploader.css";

interface DocumentUploaderProps {
  name: string;
  required?: boolean;
  requiredMessage?: string;
  maxSizeMB?: number;
  resetTrigger?: number;
}

const DocumentUploader = ({
  name,
  required = false,
  requiredMessage = "This field is required",
  maxSizeMB = 5,
  resetTrigger = 0,
}: DocumentUploaderProps) => {
  const {
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const [mainFile, setMainFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Watch the form value to sync local state (e.g. when data is loaded from API)
  const watchedFile = useWatch({ control, name });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync local state with form value (including File set via reset() before mount)
  useEffect(() => {
    if (watchedFile instanceof File) {
      setMainFile(watchedFile);
    } else if (watchedFile == null || watchedFile === "") {
      setMainFile(null);
    }
  }, [watchedFile]);

  // Handle explicit reset trigger from parent
  useEffect(() => {
    if (resetTrigger > 0) {
      setMainFile(null);
      setProgress(0);
      setValue(name, null);
      clearErrors(name);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [resetTrigger, setValue, clearErrors, name]);

  const clearProgress = () => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return prev;
        }
        return prev + 10;
      });
    }, 100);
  };

  const validateFile = (file: File) => {
    if (file.type !== "application/pdf") {
      return "Only PDF files are allowed";
    }

    const sizeInMB = file.size / (1024 * 1024);
    if (sizeInMB > maxSizeMB) {
      return `Max file size is ${maxSizeMB}MB`;
    }

    return true;
  };

  const handleFileChange = (
    file: File,
    fieldOnChange: (value: File | null) => void,
  ) => {
    const result = validateFile(file);
    if (result !== true) {
      fieldOnChange(null);
      setMainFile(null);
      alert(result);
      return;
    }

    setLoading(true);
    clearProgress();

    setTimeout(() => {
      setLoading(false);

      // File objects have no own enumerable properties (name/size/type are
      // inherited getters), so react-hook-form's deepEqual-based dirty check
      // -- which compares Object.keys() -- sees any two File instances as
      // structurally identical -- shouldDirty below is silently defeated
      // when replacing an EXISTING attachment (a File default vs. a new
      // File both look like `{}`). A null default vs. a File differs
      // correctly (different Object.keys length), which is why adding a
      // first attachment works but replacing one doesn't. Tag the instance
      // with an own enumerable property so it's always distinguishable from
      // any other File, including the one just loaded as this field's
      // default value. Deleting is unaffected by this tagging -- when no
      // attachment existed on load, the value correctly reverts to null via
      // the Controller's own onChange (handleDelete below) and isDirty
      // clears; when an attachment DID exist on load, deleting it correctly
      // leaves the form dirty (removing an attachment is a real change).
      const taggedFile = Object.assign(file, { __selectedAt: Date.now() });

      setMainFile(taggedFile);
      setValue(name, taggedFile, { shouldValidate: true, shouldDirty: true });
      clearErrors(name);
    }, 1500);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDelete = (onChange: (value: File | null) => void) => {
    setMainFile(null);
    setProgress(0);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (file: File | null) => {
          if (required && !file) return requiredMessage;
          if (file) return validateFile(file);
          return true;
        },
      }}
      render={({ field: { onChange } }) => (
        <div className="uploaderContainer">
          <div
            className={`document-uploader-box-container border-image-dashed ${loading ? " uploding-bg-color" : ""}`}
          >
            <input
              type="file"
              ref={fileInputRef}
              id="upload"
              className="upload-input"
              accept=".pdf,application/pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                handleFileChange(file, onChange);
              }}
            />
            {loading ? (
              <div className="d-flex align-items-center">
                <CircularProgress
                  size={30}
                  thickness={2}
                  color="blue"
                  progress={progress}
                />
              </div>
            ) : (
              <div className="d-flex align-items-center">
                <img
                  src={GetImages.upload}
                  alt="pdf icon"
                  className="iconsize-30 iconStyleWithWhiteFilter"
                />
              </div>
            )}
            <label htmlFor="upload" className="upload-label">
              <div className="emptyImage ">
                <div className="text-14 black">Upload Bill / Invoice</div>
                <p className="text-12 mt-1 black">
                  Max. size {maxSizeMB}MB. .pdf
                </p>
              </div>
            </label>
          </div>

          {mainFile && (
            <div className="file-actions-container w-100">
              <div className="delete-main mt-3">
                <div className="pdf-preview d-flex align-items-center gap-2 document-border-1">
                  <img
                    src={GetImages.pdfImage}
                    alt="pdf icon"
                    className="pdf-icon"
                  />
                  <p className="file-name black">{mainFile.name}</p>
                  <div
                    onClick={() => handleDelete(onChange)}
                    style={{ marginLeft: "auto", paddingRight: "8px" }}
                  >
                    <img
                      src={GetImages.deleteIconRed}
                      className="iconstyle"
                      alt="delete"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {errors[name] && (
            <div className="invalid-feedback d-block">
              {errors[name]?.message as string}
            </div>
          )}
        </div>
      )}
    />
  );
};

export default DocumentUploader;
